import {
  useState,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import Select from "react-select";

export const SelectField = forwardRef(
  (
    {
      label,
      id,
      value,
      onChange,
      options = [],
      placeholder = "Select an option",
      required = false,
      showError = true,
      optionValueKey = "id",
      optionLabelKey = "name",
      disabled = false,
      isMulti = false,
      isSearchable = true,
      isClearable = false,
      customStyles = {},
      menuPlacement = "auto",
      ...restProps
    },
    ref
  ) => {
    const [error, setError] = useState("");
    const [internalValue, setInternalValue] = useState(null);
    const [hasInteracted, setHasInteracted] = useState(false);

    // ✅ Memoize transformed options
    const transformedOptions = useMemo(() => {
      return options.map((option) => ({
        value: option[optionValueKey],
        label: option[optionLabelKey],
        ...option,
      }));
    }, [options, optionValueKey, optionLabelKey]);

    // ✅ FIXED: Better value normalization
    const normalizeValue = useCallback((val) => {
      if (!val) return [];

      // If already an array
      if (Array.isArray(val)) {
        // Handle ["1,2,3"] format
        if (
          val.length === 1 &&
          typeof val[0] === "string" &&
          val[0].includes(",")
        ) {
          return val[0]
            .split(",")
            .map((v) => v.trim())
            .filter((v) => v !== "")
            .map((v) => {
              const num = Number(v);
              return isNaN(num) ? v : num;
            });
        }

        // Handle regular array - preserve type (number or string)
        return val
          .filter((v) => v !== null && v !== undefined && v !== "")
          .map((v) => {
            // If it's already a number, keep it as number
            if (typeof v === "number") return v;
            // Try to convert to number, but keep as string if it fails
            const num = Number(v);
            return isNaN(num) ? v : num;
          });
      }

      // If it's a comma-separated string
      if (typeof val === "string" && val.includes(",")) {
        return val
          .split(",")
          .map((v) => v.trim())
          .filter((v) => v !== "")
          .map((v) => {
            const num = Number(v);
            return isNaN(num) ? v : num;
          });
      }

      // Single value
      if (typeof val === "number") return [val];
      if (typeof val === "string" && val !== "") {
        const num = Number(val);
        return [isNaN(num) ? val : num];
      }

      return [];
    }, []);

    // ✅ FIXED: getSelectedOption with better type handling
    const getSelectedOption = useCallback(
      (val) => {
        if (!val) return isMulti ? [] : null;

        if (isMulti) {
          const normalizedValues = normalizeValue(val);

          return transformedOptions.filter((opt) => {
            // Handle both string and number comparisons
            return normalizedValues.some(
              (nv) =>
                opt.value === nv ||
                String(opt.value) === String(nv) ||
                Number(opt.value) === Number(nv)
            );
          });
        } else {
          // Single select - try to match by value with type coercion
          return (
            transformedOptions.find(
              (opt) =>
                opt.value === val ||
                String(opt.value) === String(val) ||
                Number(opt.value) === Number(val)
            ) || null
          );
        }
      },
      [transformedOptions, isMulti, normalizeValue]
    );

    const validateSelect = useCallback(
      (selectValue) => {
        if (required) {
          if (isMulti) {
            if (
              !selectValue ||
              (Array.isArray(selectValue) && selectValue.length === 0)
            ) {
              return "This field is required";
            }
          } else {
            if (!selectValue) {
              return "This field is required";
            }
          }
        }
        return "";
      },
      [required, isMulti]
    );

    // ✅ Expose methods to parent via ref
    useImperativeHandle(
      ref,
      () => ({
        validate: () => {
          const validationError = validateSelect(internalValue);
          setError(validationError);
          return validationError;
        },
        touch: () => {
          setHasInteracted(true);
        },
        getValue: () => {
          if (isMulti) {
            return Array.isArray(internalValue)
              ? internalValue
                  .map((opt) => opt?.value || opt)
                  .filter((v) => v != null)
              : [];
          }
          return internalValue?.value || internalValue || "";
        },
        getError: () => error,
        isRequired: () => required,
        reset: () => {
          setInternalValue(isMulti ? [] : null);
          setError("");
          setHasInteracted(false);
        },
        getSelectedOption: () => {
          return internalValue;
        },
        focus: () => {
          const selectElement = document.querySelector(`#${id} input`);
          if (selectElement) {
            selectElement.focus();
          }
        },
      }),
      [validateSelect, internalValue, error, required, isMulti, id]
    );

    const handleChange = (selectedOption) => {
      const validationError = validateSelect(selectedOption);

      if (!hasInteracted) {
        setHasInteracted(true);
      }

      setInternalValue(selectedOption);
      setError(validationError);

      if (onChange && typeof onChange === "function") {
        let newValue;
        if (isMulti) {
          newValue = selectedOption
            ? selectedOption.map((opt) => opt.value)
            : [];
        } else {
          newValue = selectedOption ? selectedOption.value : "";
        }

        const syntheticEvent = {
          target: { id, value: newValue },
        };
        onChange(syntheticEvent);
      }
    };

    const handleBlur = () => {
      if (!hasInteracted) {
        setHasInteracted(true);
      }
      const validationError = validateSelect(internalValue);
      setError(validationError);
    };

    const handleFocus = () => {
      if (!hasInteracted) {
        setHasInteracted(true);
      }
    };

    // ✅ Sync internal value with external value changes
    useEffect(() => {
      const selectedOption = getSelectedOption(value);

      if (JSON.stringify(selectedOption) !== JSON.stringify(internalValue)) {
        setInternalValue(selectedOption);
      }
    }, [value, setInternalValue, getSelectedOption, internalValue]);

    // ✅ Validate when internal value changes
    useEffect(() => {
      if (hasInteracted) {
        const validationError = validateSelect(internalValue);
        setError(validationError);
      }
    }, [internalValue, validateSelect, hasInteracted]);

    // ✅ Memoize styles
    const mergedStyles = useMemo(() => {
      const defaultStyles = {
        control: (provided, state) => ({
          ...provided,
          borderColor:
            error && hasInteracted
              ? "#dc3545"
              : state.isFocused
              ? "#86b7fe"
              : "#ced4da",
          boxShadow: state.isFocused
            ? error && hasInteracted
              ? "0 0 0 0.25rem rgba(220, 53, 69, 0.25)"
              : "0 0 0 0.25rem rgba(13, 110, 253, 0.25)"
            : "none",
          "&:hover": {
            borderColor: error && hasInteracted ? "#dc3545" : "#86b7fe",
          },
          minHeight: "38px",
          fontSize: "0.8125rem !important",
          lineheight: "1.5",
          backgroundColor: disabled ? "#e9ecef" : "white",
        }),
        placeholder: (provided) => ({
          ...provided,
          color: "#6c757d",
        }),
        singleValue: (provided) => ({
          ...provided,
          color: "#212529",
        }),
        multiValue: (provided) => ({
          ...provided,
          backgroundColor: "#e9ecef",
        }),
        multiValueLabel: (provided) => ({
          ...provided,
          color: "#212529",
        }),
        option: (provided, state) => ({
          ...provided,
          backgroundColor: state.isSelected
            ? "#0d6efd"
            : state.isFocused
            ? "#f8f9fa"
            : "white",
          color: state.isSelected ? "white" : "#212529",
          "&:hover": {
            backgroundColor: state.isSelected ? "#0d6efd" : "#f8f9fa",
          },
        }),
      };

      return {
        ...defaultStyles,
        ...customStyles,
        control: (provided, state) => ({
          ...defaultStyles.control(provided, state),
          ...(customStyles.control
            ? customStyles.control(provided, state)
            : {}),
        }),
      };
    }, [error, hasInteracted, disabled, customStyles]);

    return (
      <div className="mb-3">
        {label ? (
          <label htmlFor={id} className="form-label">
            {label} {required ? <span className="required-field">*</span> : ""}
          </label>
        ) : (
          ""
        )}
        <Select
          inputId={id}
          value={internalValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          options={transformedOptions}
          placeholder={placeholder}
          isDisabled={disabled}
          isMulti={isMulti}
          isSearchable={isSearchable}
          isClearable={isClearable}
          styles={mergedStyles}
          menuPlacement={menuPlacement}
          className={`react-select-container ${required ? "required" : ""}`}
          classNamePrefix="react-select"
          {...restProps}
        />
        {showError && error && hasInteracted && (
          <div
            className="invalid-feedback d-block"
            style={{ fontSize: "0.875em", color: "#dc3545" }}
          >
            {error}
          </div>
        )}
      </div>
    );
  }
);

SelectField.displayName = "SelectField";
